import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Layout } from './CoreComponents';
import { getStatusMatrix } from '../store/slices/poRequestSlice';
import type { AppDispatch, RootState } from '../store';
import RequestInfoDetailModal, { type ModalRequest } from './Shared/RequestInfoDetailModal';
import BaseGrid from './Shared/BaseGrid';
import { type ColDef, type ICellRendererParams, type RowClassParams } from 'ag-grid-community';
import { useTheme } from '../contexts/ThemeContext';

/** API status-matrix: tek talep kalemi */
interface StatusMatrixItem {
  id: number;
  itemCode: string;
  itemName: string;
  productName?: string;
  requestedQuantity?: number;
  requestedDeliveryDate?: string | null;
  isUrgent?: boolean;
  note?: string | null;
  requestStatus: string;
}

/** API status-matrix: batch (parti) */
interface StatusMatrixBatch {
  createdAt: string;
  createdByName: string;
  poRequests: StatusMatrixItem[];
}

/** API response */
interface StatusMatrixResponse {
  success?: boolean;
  message?: string;
  data?: StatusMatrixBatch[];
}

/** Tablo satırı: Talep No, Ürün Bilgisi, Oluşturan, Tarih, 5 durum kolonu */
interface RequestStatusRow {
  id: number;
  productName: string;
  productCode: string;
  deliveryDate: string;
  createdByName: string;
  createdAt: string;
  rawCreatedAt: string;
  isRejected: boolean;
  /** Sıra: Talep Oluşturuldu, Yönetici Onayı Bekliyor, Yönetici Reddetti, Teklif Gönderildi, Yönetici Değerlendirmesinde, Satın Alma Siparişi Oluşturuldu */
  statuses: boolean[];
  /** API'den gelen ham durum (filtre için) */
  requestStatus: string;
  requestedQuantity?: number | undefined;
  rawRequestedDeliveryDate?: string | null | undefined;
  note?: string | null | undefined;
}

const STATUS_COLUMNS = [
  'Talep Oluşturuldu',
  'Yönetici Onayı Bekliyor',
  'Yönetici Reddetti',
  'Teklif Gönderildi',
  'Yönetici Değerlendirmesinde',
  'Satın Alma Siparişi Oluşturuldu',
] as const;

/** Durum filtresi seçenekleri (Talep durumu raporu) */
const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Tümü' },
  { value: 'pending', label: 'Yönetici onayında bekleyenler' },
  { value: 'approved', label: 'Teklif bekleyenler' },
  { value: 'rejected', label: 'Yönetici reddedilenler' },
  { value: 'convertedtoquote', label: 'Teklif gönderildi' },
  { value: 'readytoevaluate', label: 'Yönetici değerlendirmesinde' },
  { value: 'convertedtopo', label: 'Satın alma siparişi oluşturuldu' },
];

/** Rejected sütunu hiç gösterilmez; sadece satır görünümü farklılaşır */
const REJECTED_COLUMN_INDEX = 2;
const VISIBLE_STATUS_COLUMNS = STATUS_COLUMNS.filter((_, i) => i !== REJECTED_COLUMN_INDEX);
const VISIBLE_STATUS_INDICES = [0, 1, 3, 4, 5] as const;

/** requestStatus → 6 kolonluk durum (yeşil = tamamlandı) */
function requestStatusToSteps(status: string): boolean[] {
  const s = (status || '').trim().toLowerCase();
  const steps = [false, false, false, false, false, false];
  switch (s) {
    case 'draft':
      steps[0] = true;
      break;
    case 'pending':
      steps[0] = true;
      steps[1] = true;
      break;
    case 'rejected':
      steps[0] = true;
      steps[1] = true;
      steps[2] = true;
      break;
    case 'approved':
      steps[0] = true;
      steps[1] = true;
      break;
    case 'convertedtoquote':
      steps[0] = true;
      steps[1] = true;
      steps[3] = true;
      break;
    case 'readytoevaluate':
      steps[0] = true;
      steps[1] = true;
      steps[3] = true;
      steps[4] = true;
      break;
    case 'convertedtopo':
      steps[0] = true;
      steps[1] = true;
      steps[3] = true;
      steps[4] = true;
      steps[5] = true;
      break;
    default:
      steps[0] = true;
      if (['pending', 'rejected', 'approved', 'convertedtoquote', 'readytoevaluate', 'convertedtopo'].some((x) => s.includes(x) || s === x)) {
        steps[1] = true;
        if (s.includes('reject')) steps[2] = true;
        if (['convertedtoquote', 'readytoevaluate', 'convertedtopo'].some((x) => s.includes(x))) steps[3] = true;
        if (['readytoevaluate', 'convertedtopo'].some((x) => s.includes(x))) steps[4] = true;
        if (s.includes('convertedtopo') || s.includes('po')) steps[5] = true;
      }
      break;
  }
  return steps;
}

function formatDeliveryDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `Teslim: ${day}.${month}.${year}`;
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${h}:${m}`;
  } catch {
    return iso;
  }
}

/** API data → tablo satırları (batch'leri düzleştir) */
function mapStatusMatrixToRows(response: StatusMatrixResponse | null): RequestStatusRow[] {
  const batches = response?.data ?? [];
  const rows: RequestStatusRow[] = [];
  for (const batch of batches) {
    const createdByName = batch.createdByName ?? '';
    const createdAt = formatDateTime(batch.createdAt);
    for (const item of batch.poRequests ?? []) {
      const status = (item.requestStatus ?? '').trim().toLowerCase();
      rows.push({
        id: item.id,
        productName: item.itemName || item.productName || '',
        productCode: item.itemCode || '',
        deliveryDate: formatDeliveryDate(item.requestedDeliveryDate),
        createdByName,
        createdAt,
        rawCreatedAt: batch.createdAt,
        isRejected: status === 'rejected',
        statuses: requestStatusToSteps(item.requestStatus ?? ''),
        requestStatus: (item.requestStatus ?? '').trim().toLowerCase(),
        requestedQuantity: item.requestedQuantity,
        rawRequestedDeliveryDate: item.requestedDeliveryDate ?? null,
        note: item.note ?? null,
      });
    }
  }
  return rows;
}

const REQUEST_INFO_DEFAULT_COL_DEF: ColDef = {
  suppressMovable: true,
};

/** Durum sütunları için filtre/sıralama metni (BaseGrid varsayılan metin filtresi) */
function statusStepFilterValue(row: RequestStatusRow | undefined, stepIndex: number): string {
  if (!row) return '';
  if (row.isRejected && stepIndex === 0) return 'Reddedildi';
  return row.statuses[stepIndex] ? 'Tamamlandı' : 'Bekliyor';
}

const RequestInfo: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const statusMatrix = useSelector((state: RootState) => state.poRequest.statusMatrix) as StatusMatrixResponse | null;
  const isMatrixLoading = useSelector((state: RootState) => state.poRequest.operationLoading.statusMatrix);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [detailRequest, setDetailRequest] = useState<ModalRequest | null>(null);

  const openDetail = useCallback((row: RequestStatusRow) => {
    setDetailRequest({
      id: row.id,
      itemName: row.productName,
      itemCode: row.productCode,
      requestedQuantity: row.requestedQuantity,
      requestedDeliveryDate: row.rawRequestedDeliveryDate,
    });
  }, []);

  useEffect(() => {
    document.title = 'Talep Durumu';
  }, []);

  // Ekran açıldığında endpoint'i çağır
  useEffect(() => {
    dispatch(getStatusMatrix({}));
  }, [dispatch]);

  const rows = useMemo(() => mapStatusMatrixToRows(statusMatrix), [statusMatrix]);

  const filteredRows = useMemo(() => {
    let result = rows;
    // Durum filtresi
    if (statusFilter) {
      result = result.filter((row) => row.requestStatus === statusFilter);
    }
    // Arama
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (row) =>
          row.id.toString().toLowerCase().includes(term) ||
          row.productName.toLowerCase().includes(term) ||
          row.productCode.toLowerCase().includes(term)
      );
    }
    // Tarihe göre sırala: en yeni en üstte
    result = [...result].sort((a, b) => {
      const ta = a.rawCreatedAt ? new Date(a.rawCreatedAt).getTime() : 0;
      const tb = b.rawCreatedAt ? new Date(b.rawCreatedAt).getTime() : 0;
      return tb - ta;
    });
    return result;
  }, [rows, statusFilter, searchTerm]);

  const { isDarkMode } = useTheme();

  const columnDefs = useMemo<ColDef<RequestStatusRow>[]>(() => {
    const statusCell = (active: boolean) => (
      <div
        className={`w-5 h-5 rounded flex items-center justify-center ${
          active ? 'bg-[#10b981] text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500'
        }`}
      >
        {active ? <i className="fas fa-check text-[10px]" /> : <span className="text-[10px]">0</span>}
      </div>
    );

    return [
      {
        headerName: 'Talep No',
        field: 'id',
        flex: 0.4,
        minWidth: 70,
        cellRenderer: ({ data }: ICellRendererParams<RequestStatusRow>) => (
          <span className="font-medium text-gray-900 dark:text-white">#{data?.id}</span>
        ),
      },
      {
        headerName: 'Ürün Bilgisi',
        field: 'productName',
        flex: 0.9,
        minWidth: 90,
        filterValueGetter: ({ data }) =>
          [data?.productName, data?.productCode].filter(Boolean).join(' ').trim(),
        cellRenderer: ({ data }: ICellRendererParams<RequestStatusRow>) => (
          <div className="min-w-0">
            <p className="font-medium text-xs leading-tight truncate text-gray-900 dark:text-white" title={data?.productName}>
              {data?.productName || '—'}
            </p>
            <p className="text-[11px] leading-tight truncate text-gray-500 dark:text-gray-400" title={data?.productCode}>
              {data?.productCode || '—'}
            </p>
          </div>
        ),
      },
      {
        headerName: 'Oluşturan / Tarih',
        field: 'createdByName',
        flex: 0.8,
        minWidth: 100,
        filterValueGetter: ({ data }) =>
          [data?.createdByName, data?.createdAt].filter(Boolean).join(' ').trim(),
        cellRenderer: ({ data }: ICellRendererParams<RequestStatusRow>) => (
          <div>
            <p className="text-xs leading-tight text-gray-900 dark:text-white break-words">{data?.createdByName}</p>
            <p className="text-xs leading-tight text-gray-500 dark:text-gray-400 whitespace-nowrap">{data?.createdAt}</p>
          </div>
        ),
      },
      {
        headerName: 'Talep Oluşturuldu',
        colId: 'status0',
        flex: 1.5,
        minWidth: 120,
        sortable: false,
        filterValueGetter: ({ data }) => statusStepFilterValue(data, 0),
        colSpan: (params) => (params.data?.isRejected ? 5 : 1),
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: ({ data }: ICellRendererParams<RequestStatusRow>) => {
          if (data?.isRejected) {
            return (
              <div className="flex items-center text-red-700 dark:text-red-300 text-xs font-medium">
                <i className="fas fa-times-circle mr-1" />
                Yönetici tarafından reddedildi
              </div>
            );
          }
          return statusCell(!!data?.statuses[0]);
        },
      },
      {
        headerName: 'Yönetici Onayı Bekliyor',
        colId: 'status1',
        flex: 1.5,
        minWidth: 120,
        sortable: false,
        filterValueGetter: ({ data }) => statusStepFilterValue(data, 1),
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: ({ data }: ICellRendererParams<RequestStatusRow>) => statusCell(!!data?.statuses[1]),
      },
      {
        headerName: 'Teklif Gönderildi',
        colId: 'status3',
        flex: 1.5,
        minWidth: 120,
        sortable: false,
        filterValueGetter: ({ data }) => statusStepFilterValue(data, 3),
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: ({ data }: ICellRendererParams<RequestStatusRow>) => statusCell(!!data?.statuses[3]),
      },
      {
        headerName: 'Yönetici Değerlendirmesinde',
        colId: 'status4',
        flex: 1.5,
        minWidth: 120,
        sortable: false,
        filterValueGetter: ({ data }) => statusStepFilterValue(data, 4),
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: ({ data }: ICellRendererParams<RequestStatusRow>) => statusCell(!!data?.statuses[4]),
      },
      {
        headerName: 'Satın Alma Siparişi Oluşturuldu',
        colId: 'status5',
        flex: 1.5,
        minWidth: 120,
        sortable: false,
        filterValueGetter: ({ data }) => statusStepFilterValue(data, 5),
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: ({ data }: ICellRendererParams<RequestStatusRow>) => statusCell(!!data?.statuses[5]),
      },
      {
        headerName: 'Detay',
        colId: 'detail',
        flex: 0.4,
        minWidth: 65,
        sortable: false,
        filter: false,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: ({ data }: ICellRendererParams<RequestStatusRow>) => {
          const canOpen = data?.statuses[3];
          return (
            <button
              type="button"
              onClick={() => canOpen && data && openDetail(data)}
              disabled={!canOpen}
              title={canOpen ? undefined : 'Teklif gönderildikten sonra detay görüntülenebilir'}
              className={`px-2 py-1 text-[11px] font-medium border rounded transition-colors whitespace-nowrap ${
                canOpen
                  ? 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  : 'text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-60'
              }`}
            >
              <i className="fas fa-search mr-1 text-[10px]" />
              Detay
            </button>
          );
        },
      },
    ];
  }, [openDetail]);

  const getRowStyle = useCallback(
    (params: RowClassParams<RequestStatusRow>) => {
      if (!params.data?.isRejected) return undefined;
      return isDarkMode
        ? { background: 'rgba(153,27,27,0.2)' }
        : { background: 'rgba(254,226,226,0.5)' };
    },
    [isDarkMode]
  );

  const isInitialLoading = isMatrixLoading && statusMatrix === null;
  const isRefreshing = isMatrixLoading && statusMatrix !== null;

  const gridLocaleText = useMemo(
    () => ({
      noRowsToShow:
        searchTerm.trim() || statusFilter
          ? 'Arama veya filtre kriterlerinize uygun talep bulunamadı.'
          : 'Henüz talep bulunmuyor.',
    }),
    [searchTerm, statusFilter]
  );

  const emptyMessage =
    searchTerm.trim() || statusFilter
      ? 'Arama veya filtre kriterlerinize uygun talep bulunamadı.'
      : 'Henüz talep bulunmuyor.';

  return (
    <Layout currentScreen="request-info">
      <div className="max-w-8xl mx-auto h-full flex flex-col">
        {/* Üst bar: Search + Yenile (RequestTracking ile aynı) */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4 transition-colors duration-300 flex-shrink-0">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-3">
              {/* Search bar - RequestTracking ile aynı */}
              <div className="flex-1 min-w-[300px]">
                <input
                  type="text"
                  placeholder="Talep No veya Ürün Bilgisi ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-sm"
                />
              </div>
              {/* Filtreler ve Yenile - RequestTracking ile aynı konum */}
              <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[180px]"
                >
                  {STATUS_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                    dispatch(getStatusMatrix({}));
                  }}
                  disabled={isMatrixLoading}
                  className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm h-[38px] flex items-center justify-center min-w-[100px] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className={`fas fa-sync-alt mr-2 ${isMatrixLoading ? 'fa-spin' : ''}`} />
                  Yenile
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tablo - grid her zaman mount; veri gelince yeniden oluşturulmaz */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 transition-colors duration-300 flex flex-col flex-1 min-h-0 overflow-hidden relative">
          {isInitialLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-800/80">
              <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
                <i className="fas fa-spinner fa-spin text-2xl" />
                <span className="text-sm">Yükleniyor...</span>
              </div>
            </div>
          )}
          {isRefreshing && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 dark:bg-gray-800/40 pointer-events-none">
              <div className="flex items-center gap-2 bg-white dark:bg-gray-700 rounded-full shadow-md px-4 py-2 text-gray-600 dark:text-gray-300">
                <i className="fas fa-spinner fa-spin" />
                <span className="text-sm">Güncelleniyor...</span>
              </div>
            </div>
          )}

          {/* Masaüstü: AG Grid */}
          <div className="hidden lg:flex flex-col flex-1 min-h-0">
            <BaseGrid<RequestStatusRow>
              gridStateKey="requestInfo_statusGrid"
              rowData={filteredRows}
              columnDefs={columnDefs}
              defaultColDef={REQUEST_INFO_DEFAULT_COL_DEF}
              localeText={gridLocaleText}
              rowHeight={48}
              domLayout="normal"
              getRowStyle={getRowStyle}
            />
          </div>

          {/* Dar ekran: kart görünümü */}
          <div className="block lg:hidden flex-1 min-h-0 overflow-auto">
            {filteredRows.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                <i className="fas fa-inbox text-4xl mb-4 opacity-50" />
                <p>{emptyMessage}</p>
              </div>
            ) : (
              <div className="space-y-3 p-3">
                {filteredRows.map((row) => (
                  <div
                    key={row.id}
                    id={`request-card-${row.id}`}
                    className={`rounded-lg border p-4 transition-colors ${
                      row.isRejected
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span className="font-semibold text-gray-900 dark:text-white">#{row.id}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{row.createdAt}</span>
                    </div>
                    <div className="space-y-1.5 text-sm mb-3">
                      <p className="font-medium text-gray-900 dark:text-white break-words" title={row.productName}>
                        {row.productName || '—'}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 break-words" title={row.productCode}>
                        {row.productCode || '—'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{row.createdByName}</p>
                    </div>
                    {row.isRejected ? (
                      <p className="text-xs font-medium text-red-700 dark:text-red-300">
                        <i className="fas fa-times-circle mr-1" />
                        Yönetici tarafından reddedildi
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {VISIBLE_STATUS_INDICES.map((statusIdx, i) => {
                          const active = row.statuses[statusIdx];
                          const label = VISIBLE_STATUS_COLUMNS[i];
                          return (
                            <div key={statusIdx} className="flex items-center gap-1.5 text-xs">
                              <div
                                className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                  active ? 'bg-[#10b981] text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500'
                                }`}
                              >
                                {active ? <i className="fas fa-check text-[10px]" /> : <span className="text-[10px]">0</span>}
                              </div>
                              <span className="text-gray-600 dark:text-gray-400 truncate max-w-[120px]">{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <button
                        type="button"
                        onClick={() => row.statuses[3] && openDetail(row)}
                        disabled={!row.statuses[3]}
                        title={row.statuses[3] ? undefined : 'Teklif gönderildikten sonra detay görüntülenebilir'}
                        className={`text-xs font-medium border rounded px-2.5 py-1 transition-colors ${
                          row.statuses[3]
                            ? 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                            : 'text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-60'
                        }`}
                      >
                        <i className="fas fa-search mr-1" />
                        Tedarikçi Detayı
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {detailRequest && (
        <RequestInfoDetailModal
          request={detailRequest}
          onClose={() => setDetailRequest(null)}
        />
      )}
    </Layout>
  );
};

export default RequestInfo;
